import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const OG_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export default function ShareModal({ isOpen, onClose, url, title, text }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || window.location.href;
  const shareTitle = title || "Bitcoin Brain — Learn Bitcoin, Earn BTC XP";
  const shareText = text || "Check out Bitcoin Brain! Learn Bitcoin fundamentals and earn BTC XP along the way.";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const shareX = () => {
    window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  };

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");
  };

  const shareEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 w-full max-w-sm"
          data-testid="share-modal"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-lg font-bold text-white">Share</h3>
            <button onClick={onClose} className="text-zinc-500 hover:text-white">
              <X size={20} />
            </button>
          </div>

          {/* Preview */}
          <div className="bg-[#161616] border border-white/5 rounded-xl p-3 mb-6 flex items-center gap-3">
            <img src={OG_IMAGE} alt="Bitcoin Brain" className="w-12 h-12 rounded-lg" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{shareTitle}</p>
              <p className="text-zinc-500 text-xs truncate">{shareUrl}</p>
            </div>
          </div>

          {/* Platform Buttons */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <button
              onClick={shareX}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-[#161616] border border-white/5 hover:border-white/20 transition-all"
              data-testid="share-x-btn"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span className="text-zinc-400 text-[10px]">X</span>
            </button>
            <button
              onClick={shareFacebook}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-[#161616] border border-white/5 hover:border-white/20 transition-all"
              data-testid="share-facebook-btn"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#1877F2] fill-current">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-zinc-400 text-[10px]">Facebook</span>
            </button>
            <button
              onClick={shareEmail}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-[#161616] border border-white/5 hover:border-white/20 transition-all"
              data-testid="share-email-btn"
            >
              <Mail className="w-6 h-6 text-green-400" />
              <span className="text-zinc-400 text-[10px]">Email</span>
            </button>
            <button
              onClick={copyLink}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-[#161616] border border-white/5 hover:border-white/20 transition-all"
              data-testid="share-copy-btn"
            >
              {copied ? (
                <Check className="w-6 h-6 text-green-400" />
              ) : (
                <Copy className="w-6 h-6 text-zinc-400" />
              )}
              <span className="text-zinc-400 text-[10px]">{copied ? "Copied!" : "Copy"}</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Named exports for backward compatibility
export { ShareModal };
export function ShareButton({ text, url, title, className = "" }) {
  const [shareOpen, setShareOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setShareOpen(true)}
        className={`flex items-center gap-1 text-zinc-500 hover:text-[#F7931A] text-xs transition-colors ${className}`}
        data-testid="share-button"
      >
        <Copy size={12} /> Share
      </button>
      <ShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)} text={text} url={url} title={title} />
    </>
  );
}
